import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import Table from 'apollo-react/components/Table';
import IconButton from 'apollo-react/components/IconButton';
import CircularProgress from 'apollo-react/components/CircularProgress';
import OpenNew from 'apollo-react-icons/OpenNew';
import styles from './LinkedStudies.module.css';

/**
 * LinkedStudiesView - Read-only component for displaying linked studies
 *
 * Displays the list of studies that were linked when a request was created.
 * Used in request detail views (customer progress, admin review).
 *
 * @param {Object} props
 * @param {Array} props.linkedStudies - List of linked study requests
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {string} props.detailPathPrefix - Base path for viewing a linked study (default: customer progress)
 */
const LinkedStudiesView = ({
  linkedStudies = [],
  loading = false,
  error = null,
  detailPathPrefix = '/customer/progress/requests',
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        header: t('study_name'),
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id,
      },
      {
        header: t('ID'),
        accessor: 'generated_id',
      },
      {
        header: t('Workflow'),
        accessor: 'workflow_name',
      },
      {
        header: t('completionDate'),
        accessor: 'date_ended',
        customCell: ({ row }) =>
          row.date_ended ? new Date(row.date_ended).toLocaleDateString() : '-',
      },
      {
        accessor: 'actions',
        customCell: ({ row }) => {
          const requestName = row.study_name || row.generated_id;
          const handleViewDetails = (e) => {
            e.stopPropagation();
            const url = `${detailPathPrefix}/${row.id}?request_name=${encodeURIComponent(requestName)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          };
          return (
            <IconButton
              size="small"
              onClick={handleViewDetails}
              title={t('view_details')}
            >
              <OpenNew />
            </IconButton>
          );
        },
        width: 80,
      },
    ],
    [t, detailPathPrefix]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant="title2">{t('linkedStudies')}</Typography>
        {linkedStudies.length > 0 && (
          <Typography variant="bodySmall" className={styles.selectionCount}>
            {linkedStudies.length} {t('linked')}
          </Typography>
        )}
      </div>

      {linkedStudies.length === 0 ? (
        <div className={styles.emptyContainer}>
          <Typography variant="bodyDefault">{t('noLinkedStudies')}</Typography>
        </div>
      ) : (
        <Table
          columns={columns}
          rows={linkedStudies}
          rowId="id"
          hidePagination={linkedStudies.length <= 10}
        />
      )}
    </div>
  );
};

export default LinkedStudiesView;
